import { supabase } from '@/lib/supabase';
import { emitCoreNotification } from '@/lib/coreNotification';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 40. WORKFLOW ENGINE
// Infrastructure générique de workflow. Les règles spécifiques sont
// configurées par les modules, le moteur reste commun.
//   Appointment confirmed → Send notification → Create reminder
//                         → After appointment → Ask for review
//   Invoice overdue → Wait 3 days → Send reminder
// ─────────────────────────────────────────────────────────────

export const WORKFLOW_STATUSES: { key: string; label: string; className: string }[] = [
  { key: 'draft', label: 'Brouillon', className: 'bg-background-100 text-foreground-600' },
  { key: 'active', label: 'Actif', className: 'bg-primary-100 text-primary-900' },
  { key: 'paused', label: 'En pause', className: 'bg-accent-100 text-accent-900' },
];

export function workflowStatusMeta(key: string | null | undefined) {
  return WORKFLOW_STATUSES.find((s) => s.key === key) || WORKFLOW_STATUSES[0];
}

export const STEP_TYPES: { key: string; label: string; icon: string; className: string }[] = [
  { key: 'action', label: 'Action', icon: 'ri-flashlight-line', className: 'bg-primary-100 text-primary-900' },
  { key: 'wait', label: 'Attente', icon: 'ri-timer-line', className: 'bg-secondary-100 text-secondary-900' },
  { key: 'condition', label: 'Condition', icon: 'ri-git-branch-line', className: 'bg-accent-100 text-accent-900' },
  { key: 'notify', label: 'Notification', icon: 'ri-notification-3-line', className: 'bg-secondary-100 text-secondary-900' },
];

export function stepTypeMeta(key: string | null | undefined) {
  return STEP_TYPES.find((s) => s.key === key) || STEP_TYPES[0];
}

export const STEP_ACTIONS: { key: string; label: string }[] = [
  { key: 'send_notification', label: 'Envoyer une notification' },
  { key: 'send_reminder', label: 'Envoyer un rappel' },
  { key: 'create_reminder', label: 'Créer un rappel' },
  { key: 'create_task', label: 'Créer une tâche' },
  { key: 'ask_review', label: 'Demander un avis' },
  { key: 'update_status', label: 'Mettre à jour le statut' },
  { key: 'send_email', label: 'Envoyer un email' },
  { key: 'call_webhook', label: 'Appeler un webhook' },
];

export function stepActionMeta(key: string | null | undefined) {
  return STEP_ACTIONS.find((a) => a.key === key) || STEP_ACTIONS[0];
}

export const WORKFLOW_TRIGGERS: { key: string; label: string }[] = [
  { key: 'appointment.confirmed', label: 'Rendez-vous confirmé' },
  { key: 'appointment.cancelled', label: 'Rendez-vous annulé' },
  { key: 'booking.confirmed', label: 'Réservation confirmée' },
  { key: 'order.created', label: 'Commande créée' },
  { key: 'order.completed', label: 'Commande terminée' },
  { key: 'invoice.created', label: 'Facture créée' },
  { key: 'invoice.paid', label: 'Facture payée' },
  { key: 'payment.received', label: 'Paiement reçu' },
  { key: 'customer.created', label: 'Client créé' },
];

export function workflowTriggerMeta(key: string | null | undefined) {
  return WORKFLOW_TRIGGERS.find((t) => t.key === key) || { key: key || '', label: key || '—' };
}

export interface CoreWorkflow {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  name: string;
  description: string | null;
  trigger_event: string | null;
  status: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface CoreWorkflowStep {
  id: number;
  workflow_id: number;
  sort_order: number | null;
  step_type: string | null;
  action: string | null;
  label: string | null;
  config: Record<string, unknown> | null;
  delay_hours: number | null;
  created_at: string | null;
}

export interface CoreWorkflowRun {
  id: number;
  workflow_id: number;
  organization_id: number | null;
  trigger_event: string | null;
  entity_type: string | null;
  entity_id: string | null;
  status: string | null;
  started_at: string | null;
  finished_at: string | null;
  log: unknown;
  created_at: string | null;
}

export async function listWorkflows(organizationId: number): Promise<CoreWorkflow[]> {
  const { data, error } = await supabase
    .from('core_workflows')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as CoreWorkflow[];
}

export async function createWorkflow(input: {
  tenantId: number | null;
  organizationId: number;
  name: string;
  description?: string | null;
  triggerEvent: string;
}): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_workflows')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      name: input.name,
      description: input.description || null,
      trigger_event: input.triggerEvent,
      status: 'draft',
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function updateWorkflow(
  id: number,
  patch: { name?: string; description?: string | null; triggerEvent?: string; status?: string },
): Promise<void> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.name !== undefined) payload.name = patch.name;
  if (patch.description !== undefined) payload.description = patch.description || null;
  if (patch.triggerEvent !== undefined) payload.trigger_event = patch.triggerEvent;
  if (patch.status !== undefined) payload.status = patch.status;
  const { error } = await supabase.from('core_workflows').update(payload).eq('id', id);
  if (error) throw error;
}

export async function deleteWorkflow(id: number): Promise<void> {
  await supabase.from('core_workflow_steps').delete().eq('workflow_id', id);
  const { error } = await supabase.from('core_workflows').delete().eq('id', id);
  if (error) throw error;
}

export async function listWorkflowSteps(workflowId: number): Promise<CoreWorkflowStep[]> {
  const { data, error } = await supabase
    .from('core_workflow_steps')
    .select('*')
    .eq('workflow_id', workflowId)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data || []) as CoreWorkflowStep[];
}

export async function createWorkflowStep(input: {
  workflowId: number;
  sortOrder: number;
  stepType: string;
  action?: string | null;
  label: string;
  delayHours?: number | null;
}): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_workflow_steps')
    .insert({
      workflow_id: input.workflowId,
      sort_order: input.sortOrder,
      step_type: input.stepType,
      action: input.action || null,
      label: input.label,
      config: {},
      delay_hours: input.delayHours ?? null,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function deleteWorkflowStep(id: number): Promise<void> {
  const { error } = await supabase.from('core_workflow_steps').delete().eq('id', id);
  if (error) throw error;
}

export async function listWorkflowRuns(organizationId: number, workflowId?: number): Promise<CoreWorkflowRun[]> {
  let query = supabase
    .from('core_workflow_runs')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })
    .limit(50);
  if (workflowId) query = query.eq('workflow_id', workflowId);
  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as CoreWorkflowRun[];
}

/**
 * 39/40 — Workflows actifs déclenchés par un événement donné.
 * Sert de point de branchement aux consommateurs d'événements.
 */
export async function listActiveWorkflowsForEvent(
  organizationId: number,
  eventKey: string,
): Promise<CoreWorkflow[]> {
  const { data, error } = await supabase
    .from('core_workflows')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('trigger_event', eventKey)
    .eq('status', 'active');
  if (error) throw error;
  return (data || []) as CoreWorkflow[];
}

/** Résultat d'une étape exécutée par le moteur. */
export interface WorkflowStepOutcome {
  step: string;
  type: string | null;
  action: string | null;
  status: 'executed' | 'skipped' | 'waited';
  detail?: string;
}

/** Actions du moteur qui produisent une notification (registre central). */
const NOTIFYING_ACTIONS = [
  'send_notification',
  'send_reminder',
  'ask_review',
  'create_reminder',
  'create_task',
  'update_status',
  'send_email',
];

/**
 * Exécute un workflow étape par étape.
 * - mode « simulate » : journalise le déroulé sans effet de bord (bouton Simuler).
 * - mode « live » (défaut) : exécute réellement les actions (notifications…).
 */
export async function executeWorkflowRun(input: {
  workflowId: number;
  organizationId: number;
  tenantId?: number | null;
  triggerEvent: string;
  entityType?: string | null;
  entityId?: string | number | null;
  payload?: Record<string, unknown> | null;
  mode?: 'live' | 'simulate';
}): Promise<{ runId: number | null; outcomes: WorkflowStepOutcome[] }> {
  const mode = input.mode || 'live';
  const steps = await listWorkflowSteps(input.workflowId);
  const outcomes: WorkflowStepOutcome[] = [];

  for (const s of steps) {
    const base = { step: s.label || '', type: s.step_type, action: s.action };

    if (s.step_type === 'wait') {
      outcomes.push({ ...base, status: 'waited', detail: `attente ${s.delay_hours || 0} h` });
      continue;
    }
    if (s.step_type === 'condition') {
      outcomes.push({ ...base, status: 'skipped', detail: 'condition évaluée' });
      continue;
    }

    const action = s.action || '';

    if (NOTIFYING_ACTIONS.includes(action)) {
      if (mode === 'simulate') {
        outcomes.push({ ...base, status: 'executed', detail: 'simulation' });
        continue;
      }
      try {
        await emitCoreNotification({
          tenantId: input.tenantId ?? null,
          organizationId: input.organizationId,
          channel: action === 'send_email' ? 'email' : 'in_app',
          type: action === 'ask_review' ? 'system' : action,
          title: s.label || workflowTriggerMeta(input.triggerEvent).label,
          body: `Déclenché par « ${workflowTriggerMeta(input.triggerEvent).label} »`,
          entityType: input.entityType || null,
          entityId: input.entityId != null ? String(input.entityId) : null,
          status: action === 'send_email' ? 'sent' : 'pending',
          metadata: {
            workflow_id: input.workflowId,
            action,
            entity_type: input.entityType ?? null,
            entity_id: input.entityId != null ? String(input.entityId) : null,
            payload: input.payload ?? null,
          },
        });
        outcomes.push({ ...base, status: 'executed' });
      } catch {
        outcomes.push({ ...base, status: 'skipped', detail: 'échec de la notification' });
      }
      continue;
    }

    if (action === 'call_webhook') {
      outcomes.push({ ...base, status: 'skipped', detail: 'webhook non configuré' });
      continue;
    }

    outcomes.push({ ...base, status: 'skipped' });
  }

  const { data, error } = await supabase
    .from('core_workflow_runs')
    .insert({
      workflow_id: input.workflowId,
      organization_id: input.organizationId,
      trigger_event: input.triggerEvent,
      entity_type: input.entityType || null,
      entity_id: input.entityId != null ? String(input.entityId) : null,
      status: 'completed',
      started_at: new Date().toISOString(),
      finished_at: new Date().toISOString(),
      log: outcomes,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return { runId: (data?.id as number) ?? null, outcomes };
}

/**
 * Simule un workflow (bouton « Simuler ») : journalise un run sans effet réel.
 */
export async function runWorkflow(input: {
  workflowId: number;
  organizationId: number;
  tenantId?: number | null;
  triggerEvent: string;
  entityType?: string | null;
  entityId?: string | number | null;
}): Promise<number | null> {
  const res = await executeWorkflowRun({ ...input, mode: 'simulate' });
  return res.runId;
}